import { useState } from "react";
import { addDays, format, startOfToday } from "date-fns";
import { it } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Minus, Plus, ArrowLeft, Check, CalendarPlus, RotateCcw, Users, Clock } from "lucide-react";
import { RESTAURANT } from "@/demo/menuData";
import { DateStrip, SlotGrid } from "./BookingParts";
import { EASE } from "@/components/Reveal";

const firstOpenDay = () => {
  let d = startOfToday();
  while (RESTAURANT.closedDays.includes(d.getDay())) d = addDays(d, 1);
  return d;
};

const inputCls =
  "w-full rounded-xl border border-line bg-ink px-4 py-3 text-sm text-bone placeholder:text-faded transition-colors duration-300 focus:border-terracotta focus:outline-none";

const makeCode = (date, time, name) =>
  "CF-" +
  [...(format(date, "yyyyMMdd") + time + name)]
    .reduce((h, c) => (h * 33 + c.charCodeAt(0)) >>> 0, 5)
    .toString(36)
    .toUpperCase()
    .slice(0, 5);

const calendarUrl = (date, time, guests) => {
  const [h, m] = time.split(":").map(Number);
  const start = new Date(date);
  start.setHours(h, m, 0, 0);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const f = (d) => format(d, "yyyyMMdd'T'HHmmss");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Tavolo da ${RESTAURANT.name} (${guests} persone)`,
    dates: `${f(start)}/${f(end)}`,
    location: RESTAURANT.address,
  });
  return `https://calendar.google.com/calendar/render?${params}`;
};

export default function BookingDemo({ onConfirm }) {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(firstOpenDay);
  const [service, setService] = useState("dinner");
  const [time, setTime] = useState(null);
  const [guests, setGuests] = useState(2);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [booking, setBooking] = useState(null);

  const slots = service === "lunch" ? RESTAURANT.lunch : RESTAURANT.dinner;
  const raw = format(date, "EEEE d MMMM", { locale: it });
  const dateLabel = raw.charAt(0).toUpperCase() + raw.slice(1);

  const confirm = (e) => {
    e.preventDefault();
    const b = { code: makeCode(date, time, name), date, time, guests, name, phone, note, dateLabel };
    setBooking(b);
    setStep(3);
    onConfirm?.(b);
  };

  const reset = () => {
    setStep(1);
    setTime(null);
    setName("");
    setPhone("");
    setNote("");
    setBooking(null);
  };

  return (
    <div
      data-testid="booking-demo"
      className="relative overflow-hidden rounded-3xl border border-line bg-panel p-6 sm:p-8"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-terracotta">{RESTAURANT.name} · Mestre</p>
          <h3 className="mt-2 font-serif text-2xl tracking-tight text-bone">Prenota un tavolo</h3>
        </div>
        <div className="flex items-center gap-1.5 font-accent text-[10px] uppercase tracking-[0.2em] text-faded">
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              className={`h-1.5 w-6 rounded-full transition-colors duration-500 ${step >= n ? "bg-terracotta" : "bg-line"}`}
            />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="s1"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="mt-7"
          >
            <p className="mb-3 font-accent text-[10px] uppercase tracking-[0.2em] text-stone">Quando</p>
            <DateStrip selected={date} onSelect={(d) => { setDate(d); setTime(null); }} />
            <p className="mt-2 text-[11px] text-faded">Chiuso lunedì e martedì — i giorni barrati non sono prenotabili.</p>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-stone">Orario · {dateLabel}</p>
              <div className="flex rounded-full border border-line bg-ink p-0.5">
                {[
                  { id: "lunch", label: "Pranzo", icon: Sun },
                  { id: "dinner", label: "Cena", icon: Moon },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => { setService(id); setTime(null); }}
                    data-testid={`booking-service-${id}`}
                    aria-pressed={service === id}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs transition-colors duration-300 ${
                      service === id ? "bg-bone text-ink" : "text-stone hover:text-bone"
                    }`}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <SlotGrid date={date} slots={slots} selected={time} onSelect={setTime} />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-accent text-[10px] uppercase tracking-[0.2em] text-stone">Persone</p>
                <div className="mt-2 inline-flex items-center rounded-full border border-line bg-ink">
                  <button
                    type="button"
                    onClick={() => setGuests((g) => Math.max(1, g - 1))}
                    data-testid="booking-guests-minus"
                    aria-label="Meno persone"
                    className="flex h-10 w-10 items-center justify-center text-stone transition-colors hover:text-bone"
                  >
                    <Minus size={14} />
                  </button>
                  <span data-testid="booking-guests-value" className="w-8 text-center font-serif text-xl text-bone">
                    {guests}
                  </span>
                  <button
                    type="button"
                    onClick={() => setGuests((g) => Math.min(10, g + 1))}
                    data-testid="booking-guests-plus"
                    aria-label="Più persone"
                    className="flex h-10 w-10 items-center justify-center text-stone transition-colors hover:text-bone"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                {guests >= 9 && (
                  <p className="mt-2 text-[11px] text-ochre">Per gruppi oltre 8 persone il locale ti richiama per confermare.</p>
                )}
              </div>
              <button
                type="button"
                disabled={!time}
                onClick={() => setStep(2)}
                data-testid="booking-next-button"
                className="rounded-full bg-terracotta px-7 py-3.5 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Continua
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.form
            key="s2"
            onSubmit={confirm}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="mt-7"
          >
            <div className="flex flex-wrap gap-2 text-xs text-stone">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5">
                <Clock size={12} className="text-terracotta" /> {dateLabel} · {time}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5">
                <Users size={12} className="text-terracotta" /> {guests} {guests === 1 ? "persona" : "persone"}
              </span>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome e cognome"
                data-testid="booking-name-input"
                className={inputCls}
              />
              <input
                required
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Telefono"
                data-testid="booking-phone-input"
                className={inputCls}
              />
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Note: seggiolone, allergie, tavolo all'aperto…"
              data-testid="booking-note-input"
              className={`${inputCls} mt-3 resize-none`}
            />
            <div className="mt-5 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                data-testid="booking-back-button"
                className="inline-flex items-center gap-1.5 text-xs text-stone transition-colors hover:text-bone"
              >
                <ArrowLeft size={13} /> Indietro
              </button>
              <button
                type="submit"
                data-testid="booking-confirm-button"
                className="rounded-full bg-terracotta px-7 py-3.5 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a]"
              >
                Conferma prenotazione
              </button>
            </div>
          </motion.form>
        )}

        {step === 3 && booking && (
          <motion.div
            key="s3"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="mt-7 text-center"
            data-testid="booking-confirmation"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.15, type: "spring", stiffness: 260, damping: 18 }}
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cypress text-[#d6e8da]"
            >
              <Check size={30} strokeWidth={2.5} />
            </motion.div>
            <h4 className="mt-5 font-serif text-2xl tracking-tight text-bone">Tavolo confermato, {booking.name.split(" ")[0]}!</h4>
            <p className="mt-2 text-sm text-stone">
              {booking.dateLabel} alle {booking.time} · {booking.guests} {booking.guests === 1 ? "persona" : "persone"}
            </p>
            <div className="mx-auto mt-5 inline-block rounded-2xl border border-dashed border-ochre/50 px-6 py-3">
              <p className="font-accent text-[9px] uppercase tracking-[0.3em] text-faded">Codice prenotazione</p>
              <p data-testid="booking-code" className="mt-1 font-mono text-2xl tracking-[0.3em] text-ochre">
                {booking.code}
              </p>
            </div>
            <p className="mt-4 text-xs text-faded">
              Un SMS di conferma arriverebbe al {booking.phone}. Il locale riceve subito la notifica.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              <a
                href={calendarUrl(booking.date, booking.time, booking.guests)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-calendar-link"
                className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-xs text-bone transition-colors duration-300 hover:border-bone/50"
              >
                <CalendarPlus size={13} /> Aggiungi al calendario
              </a>
              <button
                type="button"
                onClick={reset}
                data-testid="booking-reset-button"
                className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-xs text-stone transition-colors duration-300 hover:text-bone"
              >
                <RotateCcw size={13} /> Nuova prenotazione
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
